import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  DataTable,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  Badge,
  SpinnerLoader,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import moment from "moment";
import { useDispatch } from "react-redux";
import { Toastify } from 'lift-ui';
import apiClient from "@lift-admin/utils/apiClient";
import { RefreshCw, Eye, X, Check } from "lucide-react";
import JsonDiff from "@lift-admin/ui/json-diff";
import axios from "axios";
import { useAuthContext } from "@lift-admin/utils/provider";
import { useConfigQuery, useDeleteReportConfig } from "./hook";
import { actionLog } from "../../App";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "@/shared/utils/trpc";
export const configApi = {
  getConfig: (reportURL: string) => {
    return trpc.reportBuilder.getConfig.query({ reportURL });
  },
};
interface RequestRow {
  id: number;
  change_table: string;
  request_json: any;
  old_json: any;
  user: {
    id: number;
    name: string;
    email: string;
    image: string;
  };
  user_approved?: {
    id: number;
    name: string;
    image: string;
  } | null;
  reason: string;
  status: string;
  created_at: string;
  updated_at: string;
}

const RequestListComponent: React.FC = () => {
  const user = JSON.parse(localStorage.getItem("super_admin") || "");
  const queryClient = useQueryClient();
  const [requests, setRequests] = useState<RequestRow[]>([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loadList, setLoadList] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [approveLoading, setApproveLoading] = useState<boolean>(false);
  const [rejectLoading, setRejectLoading] = useState<boolean>(false);

  // States for different dialogs
  const [viewRequest, setViewRequest] = useState(false);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rowSelected, setRowSelected] = useState<RequestRow | null>(null);
  const [reportExist, setReportExist] = useState<boolean>(true);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setRowsPerPage(newPageSize);
    setPage(0);
  };

  const get_orgs_ref = useRef(true);
  useEffect(() => {
    if (get_orgs_ref.current) {
      get_orgs_ref.current = false;
      getNotifications();
    }
  }, []);

  const getNotifications = () => {
    console.log("logs");
    setLoading(true);
    apiClient
      .get(`user/v1/admin/getAllRequestsForAdmin`)
      .then((res) => {
        setLoadList(false);
        setLoading(false);
        setRequests(
          res.data.response.sort((a: any, b: any) => {
            const x: any = new Date(b.updated_at);
            const y: any = new Date(a.updated_at);
            return x - y;
          })
        );
      })
      .catch((err) => {
        setLoadList(false);
        setLoading(false);
        Toastify("error", err.response.data.message);
        console.error(err);
      });
  };

  const [currentRequest, setCurrentRequest] = useState<any>({
    row: null,
    left: "",
    right: "",
  });

  const paginatedData = useMemo(() => {
    return requests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [requests, page, rowsPerPage]);

  const getStatusBadge = (status: string) => {
    if (status === "Deleted") {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300">
                {status}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>The requested report has been deleted by the user</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    } else if (status === "Pending") {
      return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">{status}</Badge>;
    } else if (status === "Approved") {
      return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">{status}</Badge>;
    } else if (status === "Rejected") {
      return <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-300">{status}</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  const handleViewRequest = (row: any) => {
    setViewRequest(true);
    setCurrentRequest({
      row: row,
      left: JSON.stringify(row.old_json || {}),
      right: JSON.stringify(row.request_json || {}),
    });
    setRowSelected(row);
  };

  const handleCheckReportRequestExist = async (row: any) => {
    const url = row.request_json.reportURL;
    // setReportURL(url);
    if (row?.status === "Deleted") {
      setReportExist(false);
    } else {
      setReportExist(true);
    }
    // handleOpenApproveDialog(row);
  };
  const handleOpenApproveDialog = async (row: any) => {
    await handleCheckReportRequestExist(row);
    setRowSelected(row);
    setApproveDialog(true);
  };

  const handleOpenRejectDialog = async (row: any) => {
    await handleCheckReportRequestExist(row);

    setRowSelected(row);
    setRejectDialog(true);
  };

  const handleUpdateAndStatus = async (status: string) => {
    if (!rowSelected) return;
    
    try {
      setLoadList(true);

      if (status === "Approved") {
        setApproveLoading(true);
      } else if (status === "Rejected") {
        setRejectLoading(true);
      }

      const res = await apiClient.put(
        `/user/v1/admin/updateRequestStatus/${rowSelected.id}`,
        {
          adminId: user.id,
          status,
        },
        {}
      );
      const previousStatus = rowSelected?.status ?? 'Pending';
      const desc = {
        message: `Report "${rowSelected?.request_json?.reportName}" request status: ${previousStatus} → ${status} (by ${user.name})`,
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: 'user_management',
        action: 'update_request_status',
        requestId: rowSelected?.id,
        changes: [{ label: 'Status', from: String(previousStatus), to: status }],
      };
      setLoadList(false);
      setViewRequest(false);
      setReportExist(true);
      setApproveDialog(false);
      setRejectDialog(false);
      getNotifications();
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err: any) {
      setLoadList(false);
      Toastify("error", err?.response?.data?.message || "Error updating request status");
      console.log(err, "REQ ERR");
      const desc = {
        message: `An error occured: ${err?.response?.data?.message}`,
        status: err.status,
        method: err.config.method,
        url: err.config.url,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), true);
    } finally {
      setApproveLoading(false);
      setRejectLoading(false);
    }
  };

  const { authUser } = useAuthContext();
  const openAdminPanel = async (rowSelected: any) => {
    const parts = rowSelected?.request_json?.reportURL?.split("reporting/");
    const reportStatus = rowSelected?.request_json?.reportStatus;
    console.log(rowSelected?.request_json, "rowSelected");
    console.log(parts, "parts");
    let reportId;
    if (parts.length > 1) {
      reportId = encodeURIComponent(parts[1]);
    }
    if (authUser) {
      const res = await apiClient.post("/affcrm/v1/user/createCustomToken", {
        uid: authUser.uid,
      });
      const token = res.data.token;
      const admin_url = `${import.meta.env.VITE_LIFT_URL}/report-builder`;
      // const admin_url = `http://localhost:3005/report-builder`;
      const adminLink = `${admin_url}/${reportId}/${reportStatus}/${rowSelected?.id}/true`;
      window.open(adminLink, "_blank");
    }
  };

  const openSessionPreview = (row: any) => {
    console.log(row, "row-value-preview");
    const requestJson = row?.request_json;
    const sessionId = requestJson?.sessionId || row?.id;
    
    // Convert name to URL-friendly slug (e.g., "Benchmark Explorer" -> "benchmark-explorer")
    const toSlug = (name: string) => {
      return name?.toLowerCase().replace(/\s+/g, "-") || "";
    };
    
    const applicationName = toSlug(requestJson?.drawerApplication?.name);
    const channelName = toSlug(requestJson?.drawerChannel?.name);
    const groupingName = requestJson?.drawerGrouping?.name ? toSlug(requestJson?.drawerGrouping?.name) : null;
    const canvasName = toSlug(requestJson?.canvasName);
    const org = 'LIFT Demo Brand';
    const brand = 'LIFT Demo Brand';
    
    // URL pattern: /{application}/{channel}/{grouping?}/{canvas}/{org}/{brand}?sessionId={sessionId}
    // Grouping is optional - only include if it exists
    const pathParts = [applicationName, channelName];
    if (groupingName) {
      pathParts.push(groupingName);
    }
    pathParts.push(canvasName);
    
    // Add org and Organization (URL encoded)
    if (org) {
      pathParts.push(encodeURIComponent(org));
    }
    if (brand) {
      pathParts.push(encodeURIComponent(brand));
    }
    
    const previewUrl = `${import.meta.env.VITE_LIFT_URL}/canvas-preview/${pathParts.join("/")}?sessionId=${sessionId}`;
    window.open(previewUrl, "_blank");
  };

  const { mutate: deployReport } = useDeleteReportConfig();
  const handleDeployHandler = () => {
    if (!rowSelected) return;
    
    setApproveLoading(true);
    if (rowSelected?.change_table === "report_builder_report_configs") {
      deployReport(
        { requestID: rowSelected?.id },
        {
          onSuccess: () => {
            handleUpdateAndStatus("Approved");
          },
          onError: (error: any) => {
            console.error("Error deploying report config:", error);
            Toastify(
              "error",
              error?.message || "Error deploying report config"
            );
            setApproveLoading(false);
          },
        }
      );
    } else {
      handleUpdateAndStatus("Approved");
    }
  };

  const dataTableColumns: TableColumn<RequestRow>[] = useMemo(
    () => [
      {
        key: "change_table",
        header: "REQUESTED FOR",
        accessor: "change_table",
        width: "12%",
        render: (_value, row) => <span className="text-sm">{row.change_table}</span>,
      },
      {
        key: "report_name",
        header: "REPORT NAME",
        accessor: (row) =>
          row.change_table === "lift_reports_sessions"
            ? row.request_json?.canvasName
            : row.request_json?.displayName,
        width: "15%",
        render: (_value, row) => (
          <span className="text-sm">
            {(row.change_table === "lift_reports_sessions"
              ? row.request_json?.canvasName
              : row.request_json?.displayName) || "N/A"}
          </span>
        ),
      },
      {
        key: "user",
        header: "REQUESTED BY",
        accessor: (row) => row.user?.name,
        width: "12%",
        align: "right",
        render: (_value, row) => (
          <div className="flex items-center justify-end gap-2">
            <span className="text-sm font-medium">{row.user?.name}</span>
            <Avatar className="h-8 w-8">
              <AvatarImage src={row.user?.image} alt={row.user?.name} />
              <AvatarFallback>{row.user?.name?.charAt(0)?.toUpperCase() || "U"}</AvatarFallback>
            </Avatar>
          </div>
        ),
      },
      {
        key: "user_approved",
        header: "APPROVED BY",
        accessor: (row) => row.user_approved?.name,
        width: "12%",
        align: "right",
        render: (_value, row) => {
          if (row.user_approved) {
            const approvedName = row.user_approved.id === user?.id ? "ME" : row.user_approved.name;
            return (
              <div className="flex items-center justify-end gap-2">
                <span className="text-sm font-medium">{approvedName}</span>
                <Avatar className="h-8 w-8">
                  <AvatarImage src={row.user_approved.image} alt={approvedName} />
                  <AvatarFallback>{approvedName?.charAt(0)?.toUpperCase() || "U"}</AvatarFallback>
                </Avatar>
              </div>
            );
          }
          return <span className="text-sm text-gray-500">Not Approved Yet</span>;
        },
      },
      {
        key: "reason",
        header: "REASON",
        accessor: "reason",
        width: "15%",
        render: (_value, row) => <span className="text-sm">{row.reason}</span>,
      },
      {
        key: "status",
        header: "STATUS",
        accessor: "status",
        width: "10%",
        render: (_value, row) => getStatusBadge(row.status),
      },
      {
        key: "actions",
        header: "CHANGE VIEW",
        accessor: (row) => row.id,
        width: "12%",
        align: "center",
        render: (_value, row) => (
          <div className="flex items-center justify-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (row?.change_table === "report_builder_report_configs") {
                        setRowSelected(row);
                        openAdminPanel(row);
                      } else if (row?.change_table === "lift_reports_sessions") {
                        openSessionPreview(row);
                      } else {
                        handleViewRequest(row);
                      }
                    }}
                    className="h-8 w-8 p-0"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Preview</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {row.status !== "Approved" &&
              row.status !== "Rejected" &&
              row.status !== "Deleted" && (
                <>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenRejectDialog(row)}
                          className="h-8 w-8 p-0"
                        >
                          <X className="h-4 w-4 text-red-600" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Reject</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenApproveDialog(row)}
                          className="h-8 w-8 p-0"
                        >
                          <Check className="h-4 w-4 text-green-600" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Approve</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
          </div>
        ),
      },
      {
        key: "created_at",
        header: "CREATED AT",
        accessor: "created_at",
        width: "10%",
        render: (_value, row) => (
          <span className="text-sm">{moment(row.created_at).format("MM/DD/YYYY")}</span>
        ),
      },
      {
        key: "updated_at",
        header: "UPDATED AT",
        accessor: "updated_at",
        width: "10%",
        render: (_value, row) => (
          <span className="text-sm">{moment(row.updated_at).format("MM/DD/YYYY")}</span>
        ),
      },
    ],
    [user]
  );

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Main dialog to view request details */}
      <Dialog open={viewRequest} onOpenChange={setViewRequest}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Request Details</DialogTitle>
          </DialogHeader>
          <div className="p-4">
              <JsonDiff
                leftJson={currentRequest.left}
                rightJson={currentRequest.right}
              />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewRequest(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval confirmation dialog */}
      <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Approval</DialogTitle>
          </DialogHeader>
          <DialogDescription>
          {!reportExist && (
              <p className="text-red-600 mb-4 text-sm">
              The report has been deleted by the user.
              </p>
          )}
            <p>
            Are you sure you want to approve this request? This action cannot be
            undone.
            </p>
          </DialogDescription>
          <DialogFooter>
          <Button
              variant="outline"
            onClick={() => {
              setReportExist(true);
              setApproveDialog(false);
            }}
            disabled={approveLoading}
          >
            Cancel
          </Button>
          <Button
              variant="default"
            onClick={() => handleDeployHandler()}
            disabled={approveLoading}
              className="bg-primary hover:bg-primary/90"
          >
              {approveLoading && <SpinnerLoader className="h-4 w-4 mr-2" />}
            Confirm Approval
          </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection confirmation dialog */}
      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Rejection</DialogTitle>
          </DialogHeader>
          <DialogDescription>
          {!reportExist && (
              <p className="text-red-600 mb-4 text-sm">
              The report has been deleted by the user.
              </p>
          )}
            <p>
            Are you sure you want to reject this request? This action cannot be
            undone.
            </p>
          </DialogDescription>
          <DialogFooter>
          <Button
              variant="outline"
            onClick={() => setRejectDialog(false)}
            disabled={rejectLoading}
          >
            Cancel
          </Button>
          <Button
              variant="destructive"
            onClick={() => handleUpdateAndStatus("Rejected")}
            disabled={rejectLoading}
          >
              {rejectLoading && <SpinnerLoader className="h-4 w-4 mr-2" />}
            Confirm Rejection
          </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page Header */}
      <div className="flex items-center justify-between px-8 py-4 border-b bg-white">
        <h1 className="text-xl font-semibold text-gray-900">Requested List</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={getNotifications}
          disabled={loading}
          className="flex items-center gap-2 h-8 px-3"
        >
        {loading ? (
            <SpinnerLoader className="h-4 w-4" />
        ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>
      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 px-8 pt-6 pb-2">
        <div className="flex-1 min-h-0 flex flex-col">
          <DataTable<RequestRow>
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="calc(100vh - 160px)"
            columns={dataTableColumns}
            data={paginatedData}
            loading={loadList}
            emptyMessage="No requests found."
            stickyHeader
            stickyFooter
            pagination={{
              currentPage: page + 1,
              totalPages: Math.max(1, Math.ceil(requests.length / rowsPerPage)),
              pageSize: rowsPerPage,
              totalItems: requests.length,
              onPageChange: (p) => handlePageChange(p - 1),
              onPageSizeChange: (size) => handlePageSizeChange(Number(size)),
              pageSizeOptions: [25, 50, 100, 200],
            }}
            showActions={false}
          />
        </div>
      </div>
    </div>
  );
};

export default RequestListComponent;
